import { html, svg, type TemplateResult } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';

import type { StickyNoteColor } from '../schemas/sticky-note-model.js';

export const STICKY_NOTE_COLOR_VALUES = {
  blue: '#a9d8df',
  green: '#bad49c',
  pink: '#efb7c8',
  yellow: '#f6d978',
} as const;

const STICKY_NOTE_PIN_COLORS = {
  blue: '#d6a33d',
  green: '#865482',
  pink: '#3d7b91',
  yellow: '#cf4f58',
} as const;

export const STICKY_NOTE_PAPER_PATHS = [
  // Top bends inward while bottom bows outward; left bows outward while the
  // opposite edge bends inward. This paired curvature reads as warped paper.
  'M .015 .015 Q .5 .045 .985 .012 Q .955 .48 .982 .95 Q .5 .995 .018 .952 Q .003 .49 .015 .015 Z',
  // Exact directional inverse of variant 0.
  'M .015 .015 Q .5 .003 .985 .012 Q .997 .48 .982 .95 Q .5 .915 .018 .952 Q .045 .49 .015 .015 Z',
  // Vertical pair follows variant 0, horizontal pair follows variant 1.
  'M .015 .015 Q .5 .045 .985 .012 Q .997 .48 .982 .95 Q .5 .995 .018 .952 Q .045 .49 .015 .015 Z',
  // Vertical pair follows variant 1, horizontal pair follows variant 0.
  'M .015 .015 Q .5 .003 .985 .012 Q .955 .48 .982 .95 Q .5 .915 .018 .952 Q .003 .49 .015 .015 Z',
] as const;

export type StickyNoteVisualOptions = {
  color: StickyNoteColor;
  height: number | string;
  paperVariant: number;
  pinOffset: number;
  pinOffsetY: number;
  pinRotation: number;
  position?: 'absolute' | 'relative';
  rotation: number;
  visualId: string;
  width: number | string;
};

const cssLength = (value: number | string) =>
  typeof value === 'number' ? `${value}px` : value;

/** Shared paper silhouette used by both the drag ghost and canvas block. */
export function renderStickyNotePaper({
  color,
  height,
  paperVariant,
  pinOffset,
  pinOffsetY,
  pinRotation,
  position = 'relative',
  rotation,
  visualId,
  width,
}: StickyNoteVisualOptions): TemplateResult {
  const safePinOffset = Number.isFinite(pinOffset) ? pinOffset : 0;
  const safePinOffsetY = Number.isFinite(pinOffsetY) ? pinOffsetY : 0;
  const safePinRotation = Number.isFinite(pinRotation) ? pinRotation : 0;
  const path = STICKY_NOTE_PAPER_PATHS[paperVariant % 4];
  const gradientId = `${visualId}-light`;

  return html`<div
    class="sticky-note-paper-visual"
    data-color=${color}
    data-paper-variant=${paperVariant}
    style=${styleMap({
      filter: [
        'drop-shadow(1px 3px 3px rgb(0 0 0 / 16%)) drop-shadow(5px 15px 16px rgb(0 0 0 / 17%))',
        'drop-shadow(-3px 4px 3px rgb(0 0 0 / 17%)) drop-shadow(4px 14px 15px rgb(0 0 0 / 16%))',
        'drop-shadow(3px 4px 3px rgb(0 0 0 / 17%)) drop-shadow(6px 14px 15px rgb(0 0 0 / 16%))',
        'drop-shadow(0 5px 4px rgb(0 0 0 / 18%)) drop-shadow(3px 13px 15px rgb(0 0 0 / 15%))',
      ][paperVariant % 4],
      height: cssLength(height),
      inset: position === 'absolute' ? '0' : undefined,
      position,
      transform: `rotate(${rotation}deg)`,
      transformOrigin: 'center',
      width: cssLength(width),
    })}
  >
    ${svg`<svg
      class="sticky-note-paper"
      width="100%"
      height="100%"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id=${gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="white" stop-opacity=".16"></stop>
          <stop offset=".42" stop-color="white" stop-opacity="0"></stop>
          <stop offset="1" stop-color="#5a421d" stop-opacity=".055"></stop>
        </linearGradient>
      </defs>
      <path d=${path} fill=${STICKY_NOTE_COLOR_VALUES[color]}></path>
      <path d=${path} fill=${`url(#${gradientId})`}></path>
    </svg>`}
    <div
      class="sticky-note-pin"
      style=${styleMap({
        aspectRatio: '1',
        left: `${50 + safePinOffset * 100}%`,
        pointerEvents: 'none',
        position: 'absolute',
        top: `${7 + safePinOffsetY * 100}%`,
        transform: `translateX(-50%) rotate(${safePinRotation}deg)`,
        transformOrigin: 'center',
        width: '3.5%',
        zIndex: '4',
      })}
      aria-hidden="true"
    >
      <span
        class="sticky-note-pin-shadow"
        style="
          position: absolute;
          z-index: 0;
          top: 31%;
          left: 12%;
          width: 112%;
          height: 92%;
          border-radius: 50%;
          background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.34) 0%, rgba(0, 0, 0, 0.2) 38%, rgba(0, 0, 0, 0.08) 58%, rgba(0, 0, 0, 0) 76%);
        "
      ></span>
      <span
        class="sticky-note-pin-head"
        style=${styleMap({
          background: `radial-gradient(circle at 34% 27%, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.64) 13%, ${STICKY_NOTE_PIN_COLORS[color]} 42%, ${STICKY_NOTE_PIN_COLORS[color]} 68%, rgba(0, 0, 0, 0.48) 100%)`,
          borderRadius: '50%',
          inset: '0',
          position: 'absolute',
          zIndex: '1',
        })}
      ></span>
    </div>
  </div>`;
}
